import { QueryParams } from "@/types/network";
import { createSelector } from "reselect";
import { RootState } from "@lib/redux/types";
import { apiSlice } from "@features/application/api-slice";
import { selectCurrentEventId } from "@features/application/app-selectors";
import { eventsApi } from "@features/events/events-api";
import { selectUserWorkspaces } from "@features/workspaces/workspaces-selectors";

/**
 * Selector that aggregates events from all user workspaces with optional filtering.
 * This selector combines events from multiple workspaces into a single flat array,
 * making it easier to display all user events in dashboard or list views.
 *
 * @param query - Optional filtering parameters to apply to events query (e.g., stages, status)
 * @returns A memoized selector that returns a flattened array of events from all user workspaces
 */
export const selectWorkspacesEvents = (query?: QueryParams) =>
  createSelector(
    [selectUserWorkspaces, (state: RootState) => state[apiSlice.reducerPath]],
    (workspaces, apiState) =>
      // Flatten events from all workspaces into a single array
      workspaces?.flatMap(
        (workspace) =>
          // Get cached events for each workspace using the API selector
          // Returns an empty array if events are not loaded yet or the request failed
          eventsApi.endpoints.getWorkspaceEvents.select({
            idOrSlug: workspace.id,
            query,
          })({ [apiSlice.reducerPath]: apiState } as RootState).data ?? [],
      ),
  );

/**
 * Selector that retrieves the currently active event based on the current event ID.
 * This selector is used throughout the application to access details of the event
 * that the user is currently viewing or working with.
 *
 * @returns The current event object or undefined if no event is selected or not loaded
 */
export const selectCurrentEvent = createSelector(
  [selectCurrentEventId, (state: RootState) => state[apiSlice.reducerPath]],
  (currentEventId, apiState) =>
    // Return undefined if no event is currently selected
    currentEventId === null
      ? undefined
      : // Use API selector to get cached event data by ID
        // Convert eventId to number to ensure cache key matches hook usage
        eventsApi.endpoints.getEvent.select({ eventId: Number(currentEventId) })({
          [apiSlice.reducerPath]: apiState,
        } as RootState).data,
);
